/**
 * Trip CO2 Calculator — inputs panel.
 *
 * Controlled form. Parent (`TripCalculatorPage`) owns all state; the
 * panel just renders fields and forwards changes. This separation
 * keeps the panel reusable (e.g. on a future server-rendered share
 * page) and trivial to test.
 *
 * Layout policy (mobile-first):
 *   • Labels render *inline* with their field (label on the left,
 *     field on the right) to keep vertical space tight.
 *   • Transport + "Round trip" share a single row so the toggle sits
 *     visually next to the mode it qualifies.
 *   • Sub-config rows (passengers, fuel) follow the same inline-label
 *     pattern and only render for modes that have editable sub-fields.
 *
 * Sub-config rendering is data-driven from `modes.ts`:
 *   USER_EDITABLE_KEYS  → which controls to show
 *   OCCUPANCY_RANGES    → number-input bounds
 *   CAR_FUELS           → fuel options for car
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/button';
import Checkbox from '../../components/ui/Checkbox';
import Select from '../../components/ui/Select';
import CitySearchInput from '../geocoding/CitySearchInput';
import type { CityResult } from '../geocoding/api';
import {
  CAR_FUELS,
  MODE_DISPLAY,
  MODE_ORDER,
  OCCUPANCY_RANGES,
  USER_EDITABLE_KEYS,
} from './modes';
import type { ConfigByMode, TransportMode } from './modes';
import { searchCitiesPublic } from './geocodingApi';

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface InputsPanelProps {
  origin: CityResult | null;
  destination: CityResult | null;
  /**
   * `null` until the user picks a transport mode. The dropdown opens
   * on a placeholder option so there's no implicit default — the
   * page-level `canCalculate` keeps the action button off until a
   * real mode is chosen.
   */
  mode: TransportMode | null;
  config: ConfigByMode[TransportMode] | null;
  roundTrip: boolean;
  isCalculating: boolean;
  /**
   * True iff origin/destination are both set and the calculate button
   * should be enabled. Hoisted into props so the page can layer
   * other validations later (e.g. quota gate) without changing this
   * component's contract.
   */
  canCalculate: boolean;
  /**
   * True iff the inputs match the last-submitted (or URL-deeplinked)
   * trip exactly. When true *and* `canCalculate` is true, the button
   * renders in a muted state but remains clickable — clicking it
   * fires `onCalculate`, which the parent uses to surface the hint
   * message instead of running a redundant calculation.
   */
  unchangedSinceSubmit?: boolean;
  /**
   * Optional inline hint to render under the Calculate button.
   * Currently used for the "Edit this travel to recalculate" message
   * triggered by clicking the muted button. Null/undefined → no hint.
   */
  calculateHint?: string | null;
  onOriginChange: (city: CityResult | null) => void;
  onDestinationChange: (city: CityResult | null) => void;
  onModeChange: (mode: TransportMode) => void;
  onConfigChange: (config: ConfigByMode[TransportMode]) => void;
  onRoundTripChange: (rt: boolean) => void;
  onCalculate: () => void;
  /**
   * When provided, renders a small "×" close affordance at the
   * top-right of the panel so the user can dismiss the form view and
   * return to the collapsed summary card without recalculating.
   * Only meaningful when there's a previously submitted trip to go
   * back to — the page omits this prop until that condition is met.
   */
  onCancel?: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline-label row primitive
// ─────────────────────────────────────────────────────────────────────────────

const LABEL_WIDTH = 84;
const ROW_GAP = 10;

/**
 * One input row with a label on the left and the field on the right.
 * Label width is fixed so multiple stacked rows align vertically. The
 * field area flexes to fill the rest.
 *
 * On extremely narrow viewports (sub-360px) the row stays inline; the
 * label + 220px field still fits and the alternative (stacking labels
 * above) cost more vertical space than the calculator can spare.
 */
const Row: React.FC<{
  label?: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: ROW_GAP,
      marginBottom: 10,
    }}
  >
    {label !== undefined && (
      <span
        style={{
          flex: `0 0 ${LABEL_WIDTH}px`,
          fontSize: 13,
          fontWeight: 600,
          color: '#475569',
          textAlign: 'right',
        }}
      >
        {label}
      </span>
    )}
    <div style={{ flex: '1 1 auto', minWidth: 0 }}>{children}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const InputsPanel: React.FC<InputsPanelProps> = ({
  origin,
  destination,
  mode,
  config,
  roundTrip,
  isCalculating,
  canCalculate,
  unchangedSinceSubmit = false,
  calculateHint = null,
  onOriginChange,
  onDestinationChange,
  onModeChange,
  onConfigChange,
  onRoundTripChange,
  onCalculate,
  onCancel,
}) => {
  // Inline T&C / Privacy Policy acknowledgement (Ryanair pattern).
  // The checkbox is a transparency hint — submitting via the Calculate
  // button is the affirmative act of acceptance and auto-ticks the box.
  // No "must check to enable" gating; the contract is the click.
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Three button states:
  //   • "ready"  — fully usable (teal). Click runs calculation.
  //   • "muted"  — looks disabled but is still clickable. Click fires
  //     `onCalculate` and the parent shows a hint instead of running.
  //   • "off"    — truly disabled (HTML `disabled`). No click fires.
  //
  // Computing them locally keeps the page free of button-style logic.
  const isOff = !canCalculate || isCalculating;
  const isMuted = canCalculate && unchangedSinceSubmit && !isCalculating;
  return (
    <section
      className="trip-calc-inputs"
      aria-label="Trip inputs"
      style={{ position: 'relative' }}
    >
      {onCancel && (
        // Small close affordance — only renders when the parent says
        // there's a previous calculation to return to. Anchored to the
        // section's top-right corner so it doesn't push any form rows
        // around.
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel editing and return to trip summary"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            padding: 0,
            fontSize: 16,
            lineHeight: 1,
            color: '#64748b',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 999,
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#0f172a';
            e.currentTarget.style.borderColor = '#cbd5e1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#64748b';
            e.currentTarget.style.borderColor = '#e2e8f0';
          }}
        >
          ×
        </button>
      )}
      <Row label="From">
        <CitySearchInput
          initialLabel={origin?.displayName}
          placeholder="e.g. Berlin"
          searchFn={searchCitiesPublic}
          onSelect={onOriginChange}
        />
      </Row>

      <Row label="To">
        <CitySearchInput
          initialLabel={destination?.displayName}
          placeholder="e.g. Paris"
          searchFn={searchCitiesPublic}
          onSelect={onDestinationChange}
        />
      </Row>

      <Row label="Transport">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Select
            value={mode ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              // The placeholder option is `disabled`, but guard anyway
              // so a stray empty value never reaches the parent.
              if (v === '') return;
              onModeChange(v as TransportMode);
            }}
            style={{
              flex: '1 1 auto',
              minWidth: 0,
              // Mute the field when no mode is picked so it visually
              // reads as "needs your input" instead of a real value.
              color: mode === null ? '#94a3b8' : undefined,
            }}
          >
            <option value="" disabled hidden>
              Select transport…
            </option>
            {MODE_ORDER.map((m) => (
              <option key={m} value={m} style={{ color: '#0f172a' }}>
                {MODE_DISPLAY[m].emoji} {MODE_DISPLAY[m].label}
              </option>
            ))}
          </Select>
          {/* The checkbox keeps its own inline label; sits next to the
              select so the toggle reads as "transport, optionally round
              trip" without taking a separate vertical slot. */}
          <div style={{ flex: '0 0 auto' }}>
            <Checkbox
              checked={roundTrip}
              onChange={(e) => onRoundTripChange(e.target.checked)}
              label="Round trip"
            />
          </div>
        </div>
      </Row>

      <ModeSubConfig mode={mode} config={config} onChange={onConfigChange} />

      {/* Full-width primary action — Jody's standard pattern for the
          one "do the thing" button on a form. Larger hit target on
          mobile + unambiguous visual weight on desktop. */}
      <div className="trip-calc-inputs-cta" style={{ marginTop: 14 }}>
        <Button
          variant="primary"
          // Truly disabled when there's nothing to do at all. "Muted"
          // (clickable-but-styled-as-off) when there *is* a trip on
          // screen but it hasn't changed since the last submission —
          // the parent uses that click to surface the inline hint.
          disabled={isOff}
          onClick={() => {
            // Clicking Calculate is itself the affirmative act of
            // accepting the Terms + Privacy Policy (Ryanair-pattern
            // click-wrap). The checkbox below is a transparency hint,
            // not a separate gate — auto-tick it on submit so it
            // visually reflects what the click already implied. We
            // also auto-tick the local box for users who toggled it
            // off and then clicked anyway.
            if (!termsAccepted) setTermsAccepted(true);
            onCalculate();
          }}
          style={{
            width: '100%',
            padding: '0.85rem',
            fontSize: 15,
            ...(isMuted && {
              opacity: 0.55,
              cursor: 'not-allowed',
            }),
          }}
        >
          {isCalculating ? 'Calculating…' : 'Calculate'}
        </Button>
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginTop: 10,
            fontSize: 12.5,
            color: '#475569',
            lineHeight: 1.45,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            style={{ marginTop: 2, flexShrink: 0 }}
          />
          <span>
            By clicking <strong>Calculate</strong>, I accept the{' '}
            <Link to="/terms-and-conditions" style={{ color: '#0f766e', textDecoration: 'underline' }}>
              Terms of Use
            </Link>{' '}
            and{' '}
            <Link to="/privacy-policy" style={{ color: '#0f766e', textDecoration: 'underline' }}>
              Privacy Policy
            </Link>
            .
          </span>
        </label>
        {calculateHint && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 6,
              textAlign: 'center',
              fontSize: 12.5,
              color: '#64748b',
              fontStyle: 'italic',
              lineHeight: 1.4,
            }}
          >
            {calculateHint}
          </div>
        )}
      </div>
    </section>
  );
};

export default InputsPanel;

// ─────────────────────────────────────────────────────────────────────────────
// Mode sub-config (per-mode editable fields, rendered as inline-label rows)
// ─────────────────────────────────────────────────────────────────────────────

interface ModeSubConfigProps {
  mode: TransportMode | null;
  config: ConfigByMode[TransportMode] | null;
  onChange: (config: ConfigByMode[TransportMode]) => void;
}

const ModeSubConfig: React.FC<ModeSubConfigProps> = ({ mode, config, onChange }) => {
  if (mode === null || config === null) return null;
  if (USER_EDITABLE_KEYS[mode].length === 0) return null;

  if (mode === 'car') {
    const c = config as ConfigByMode['car'];
    return (
      // Custom row: car has two short fields, so we put Passengers
      // and Fuel on the same line to save vertical space. The first
      // label keeps the standard 84px width to align with the rows
      // above; the inline "Fuel" label hugs its select.
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 10,
        }}
      >
        <span
          style={{
            flex: `0 0 ${LABEL_WIDTH}px`,
            fontSize: 13,
            fontWeight: 600,
            color: '#475569',
            textAlign: 'right',
          }}
        >
          Passengers
        </span>
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <OccupancyInput
            value={c.occupancy}
            range={OCCUPANCY_RANGES.car}
            onChange={(occupancy) => onChange({ ...c, occupancy })}
          />
        </div>
        <span
          style={{
            flex: '0 0 auto',
            fontSize: 13,
            fontWeight: 600,
            color: '#475569',
            paddingLeft: 4,
          }}
        >
          Fuel
        </span>
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <Select
            value={c.fuel}
            onChange={(e) =>
              onChange({ ...c, fuel: e.target.value as ConfigByMode['car']['fuel'] })
            }
          >
            {CAR_FUELS.map((f) => (
              <option key={f} value={f}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </option>
            ))}
          </Select>
        </div>
      </div>
    );
  }

  if (mode === 'van') {
    const c = config as ConfigByMode['van'];
    return (
      <Row label="Passengers">
        <OccupancyInput
          value={c.occupancy}
          range={OCCUPANCY_RANGES.van}
          onChange={(occupancy) => onChange({ ...c, occupancy })}
        />
      </Row>
    );
  }

  if (mode === 'motorcycle') {
    const c = config as ConfigByMode['motorcycle'];
    return (
      <Row label="Riders">
        <OccupancyInput
          value={c.occupancy}
          range={OCCUPANCY_RANGES.motorcycle}
          onChange={(occupancy) => onChange({ ...c, occupancy })}
        />
      </Row>
    );
  }

  return null;
};

interface OccupancyInputProps {
  value: number;
  range: { min: number; max: number };
  onChange: (n: number) => void;
}

const OccupancyInput: React.FC<OccupancyInputProps> = ({ value, range, onChange }) => {
  const options: number[] = [];
  for (let n = range.min; n <= range.max; n++) options.push(n);
  return (
    <Select value={value} onChange={(e) => onChange(Number(e.target.value))}>
      {options.map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </Select>
  );
};
